import dayjs from 'dayjs';
import { ConfigCustomFieldDto } from 'korint-sdk';
import type {
  Field,
  FieldShortcut,
  FormSections,
  FormSectionsWithShortcuts,
} from 'korint-sdk/react';
import { CountryCode } from 'libphonenumber-js/max';
import _, { isNil } from 'lodash';
import {
  DEFAULT_VEHICLE_IDENTITY_FIELDS,
  VEHICLE_SELECTION_RESOLVED_FIELD,
} from '../../features/vehicle-input/constants';

const extractCountriesRule = (
  validationRules: ConfigCustomFieldDto['validationRules'],
): CountryCode[] | undefined =>
  validationRules?.find(({ option }) => option === 'countries')?.value as CountryCode[] | undefined;

function isFieldDirty(field: Field, dirtyFields: Record<string, boolean | undefined>) {
  if (field.type === 'computed') {
    if (field.dependsOn.length === 0) {
      return true;
    }

    return field.dependsOn.some(dependsOnField => dependsOnField in dirtyFields);
  }

  return _.get(dirtyFields, field.name);
}

export function areFieldsDirty(fields: Field[], dirtyFields: Record<string, boolean | undefined>) {
  return fields.some(field => isFieldDirty(field, dirtyFields));
}

/**
 * A default the entity has no value for is marked dirty so the submit persists it — the dirty gate
 * would otherwise drop a value the broker was shown but never touched.
 */
export function markSeededDefaultsDirty(
  fields: Field[],
  data: Record<string, unknown> | undefined,
  dirtyFields: Record<string, boolean | undefined>,
): Record<string, boolean | undefined> {
  const seeded = fields.filter(
    field =>
      field.defaultValue !== undefined &&
      !('ignoreInPayload' in field && field.ignoreInPayload) &&
      readEntityValue(field, data) === undefined,
  );

  if (seeded.length === 0) return dirtyFields;

  return { ...dirtyFields, ...Object.fromEntries(seeded.map(({ name }) => [name, true])) };
}

const toArrayRows = (value: unknown) =>
  (Array.isArray(value) ? value : [])
    .map(row => _.omitBy(row as Record<string, unknown>, cell => cell === undefined || cell === ''))
    .filter(row => !_.isEmpty(row));

export function buildPayloadFromFields<TFields extends Array<Field>>(
  fields: TFields,
  data: Record<string, unknown> | undefined,
  dirtyFields?: Record<string, boolean | undefined>,
) {
  return fields.reduce(
    (acc, field) => {
      if ('ignoreInPayload' in field && field.ignoreInPayload) {
        return acc;
      }

      if (dirtyFields && !isFieldDirty(field, dirtyFields)) {
        return acc;
      }

      if (typeof field.formDataAccessor === 'function') {
        const manipulatedData = field.formDataAccessor(data ?? {});

        if (field.isCustomField) {
          acc.customFields ??= [];
          const customFields = acc.customFields as Array<{ key: string; value: unknown }>;
          const key =
            'payloadKeyName' in field && field.payloadKeyName ? field.payloadKeyName : field.name;

          if (
            field.canBeDeleted &&
            (manipulatedData === null || manipulatedData === undefined || manipulatedData === '')
          ) {
            customFields.push({ key, value: null });
          } else if (manipulatedData !== undefined) {
            customFields.push({ key, value: manipulatedData });
          }
        } else {
          Object.assign(acc, manipulatedData);
        }

        return acc;
      }

      const rawValue = _.get(data, field.formDataAccessor ?? field.name);

      if (field.type === 'array') {
        const rows = toArrayRows(rawValue);
        acc.customFields ??= [];
        (acc.customFields as Array<{ key: string; value: unknown }>).push({
          key: field.payloadKeyName ?? field.name,
          value: rows.length > 0 ? rows : null,
        });
        return acc;
      }

      // Option-backed values must be sent exactly as provided: Hiscox activity
      // enum values carry trailing spaces that must match backend validation
      // and external rater spreadsheets.
      const normalized =
        typeof rawValue === 'string' &&
        !['select', 'multiSelect', 'hiscoxActivity'].includes(field.type)
          ? rawValue.trim()
          : rawValue;
      let value = normalized;
      if (field.type === 'number') {
        // Skip optional number fields that are empty
        if (isNil(value)) {
          return acc;
        }
        value = Number(value);
      } else if (typeof normalized === 'string' && field.type === 'datetime') {
        value = dayjs(normalized).toISOString();
      } else if (typeof normalized === 'string' && field.type === 'month') {
        const d = dayjs(normalized);
        value = d.isValid() ? d.startOf('month').format('YYYY-MM-DD') : undefined;
      }

      // Never send '' for a non-clearable/non-deletable field: strict validation
      // (e.g. enum custom fields) rejects it and it would wrongly blank the value.
      // Clearing is opt-in via `clearable`/`canBeDeleted`, handled below.
      if (value === '' && !field.clearable && !field.canBeDeleted) {
        return acc;
      }

      if (field.isCustomField) {
        acc.customFields ??= [];
        const customFields = acc.customFields as Array<{ key: string; value: unknown }>;
        const key =
          'payloadKeyName' in field && field.payloadKeyName ? field.payloadKeyName : field.name;

        if (field.canBeDeleted && (value === null || value === undefined || value === '')) {
          customFields.push({ key, value: null });
        } else if (value !== undefined && value !== null) {
          customFields.push({ key, value });
        }
      } else {
        Object.assign(acc, { [field.name]: value });
      }

      return acc;
    },
    {} as Record<string, unknown>,
  );
}

// An unparseable stored value (e.g. an integration writing '') would format to the literal
// "Invalid Date" and surface as such in inputs and tables.
function formatDateValue(value: string, format: string): string {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : '';
}

function readEntityValue(field: Field, data: Record<string, unknown> | undefined) {
  if (typeof field.apiDataAccessor === 'function') {
    return field.apiDataAccessor(data ?? {});
  }

  const fieldPath = field.apiDataAccessor?.split('.') ?? [field.name];

  // An explicit apiDataAccessor is an absolute read path; only fields that omit it default to customFields.<name>.
  if (field.isCustomField && field.apiDataAccessor === undefined) {
    fieldPath.unshift('customFields');
  }

  return _.get(data, fieldPath);
}

export function buildFormValuesFromFields<TFields extends Array<Field>>(
  fields: TFields,
  data: Record<string, unknown> | undefined,
) {
  return fields.reduce(
    (acc, field) => {
      if (field.type === 'computed') {
        return acc;
      }

      const rawValue = readEntityValue(field, data);

      const value =
        typeof rawValue === 'string' && field.type === 'date'
          ? formatDateValue(rawValue, 'YYYY-MM-DD')
          : typeof rawValue === 'string' && field.type === 'datetime'
            ? formatDateValue(rawValue, 'YYYY-MM-DDTHH:mm')
            : typeof rawValue === 'string' && field.type === 'month'
              ? formatDateValue(rawValue, 'YYYY-MM')
              : rawValue;

      acc[field.name] = value === undefined ? field.defaultValue : value;

      return acc;
    },
    {} as Record<string, unknown>,
  );
}

const resolveFieldShortcutFromConfig = (
  shortcut: FieldShortcut,
  customFieldsConfig: ConfigCustomFieldDto[] = [],
): Field | null => {
  const isStringShortcut = typeof shortcut === 'string';
  const fieldKey = isStringShortcut ? shortcut : shortcut.name;

  const platformFields: ConfigCustomFieldDto[] = [
    {
      key: 'startedAt',
      requiredFor: 'QUOTE',
      type: 'DATETIME',
    },
    {
      key: 'name',
      requiredFor: 'NOTHING',
      type: 'STRING',
    },
  ];

  const fieldConfig = [...platformFields, ...customFieldsConfig].find(
    ({ key }) => key === fieldKey,
  );

  if (!fieldConfig) {
    if (isStringShortcut) {
      return null;
    }

    return shortcut as Field;
  }

  const { type, validationRules, requiredFor, canBeDeleted } = fieldConfig;

  const baseField: Omit<Field, 'type'> = {
    name: isStringShortcut ? fieldConfig.key : shortcut.name,
    isCustomField: !platformFields.find(({ key }) => key === fieldKey),
    required: Boolean(requiredFor) && requiredFor !== 'NOTHING' && requiredFor !== 'NEVER',
    canBeDeleted,
  };

  // Have to omit 'type' attribute to avoid typescript going crazy in the switch statement
  const overrideConfig = !isStringShortcut ? (shortcut as Omit<Field, 'type'>) : {};

  const forcedType =
    !isStringShortcut && 'type' in (shortcut as Field) ? (shortcut as Field).type : null;

  switch (type) {
    case 'BOOLEAN': {
      if (forcedType === 'checkbox') {
        return {
          ...baseField,
          type: forcedType,
          ...overrideConfig,
        };
      }

      return {
        ...baseField,
        type: 'radio',
        options: ['true', 'false'],
        apiDataAccessor: ({ customFields }) => (customFields?.[baseField.name] ? 'true' : 'false'),
        formDataAccessor: data => data[baseField.name] === 'true',
        ...overrideConfig,
      };
    }
    case 'STRING': {
      return {
        ...baseField,
        type: 'text',
        ...overrideConfig,
      };
    }
    case 'NUMBER':
    case 'INTEGER': {
      return {
        ...baseField,
        type: 'number',
        integer: type === 'INTEGER',
        min: validationRules?.find(({ option }) => option === 'min')?.value as number | undefined,
        max: validationRules?.find(({ option }) => option === 'max')?.value as number | undefined,
        ...overrideConfig,
      };
    }
    case 'ENUM': {
      return {
        ...baseField,
        type: 'select',
        options:
          (fieldConfig.validationRules?.find(({ option }) => option === 'enum')?.value as
            | string[]
            | undefined) ?? [],
        ...overrideConfig,
      };
    }
    case 'DATE': {
      let min: string | undefined;
      let max: string | undefined;

      if (validationRules) {
        for (const rule of validationRules) {
          const ruleValue = rule.value as number;
          if (rule.option === 'monthsGreaterThanOrEqual') {
            // Must be at least N months old → birth date ≤ today - N months
            max = dayjs().subtract(ruleValue, 'month').format('YYYY-MM-DD');
          } else if (rule.option === 'yearsLessThan') {
            // Must be less than N years old → birth date > today - N years
            min = dayjs().subtract(ruleValue, 'year').add(1, 'day').format('YYYY-MM-DD');
          } else if (rule.option === 'yearsGreaterThanOrEqual') {
            // Must be at least N years old → birth date ≤ today - N years
            max = dayjs().subtract(ruleValue, 'year').format('YYYY-MM-DD');
          } else if (rule.option === 'monthsLessThan') {
            // Must be less than N months old → birth date > today - N months
            min = dayjs().subtract(ruleValue, 'month').add(1, 'day').format('YYYY-MM-DD');
          }
        }
      }

      return {
        ...baseField,
        type: 'date',
        ...(min && { min }),
        ...(max && { max }),
        ...overrideConfig,
      };
    }
    case 'DATETIME': {
      return {
        ...baseField,
        type: 'datetime',
        ...overrideConfig,
      };
    }
    case 'PHONE_NUMBER': {
      return {
        ...baseField,
        type: 'phoneNumber',
        countries: extractCountriesRule(validationRules),
        ...overrideConfig,
      };
    }
    case 'POSTCODE': {
      return {
        ...baseField,
        type: 'postcode',
        countries: extractCountriesRule(validationRules),
        ...overrideConfig,
      };
    }
    case 'EMAIL': {
      return {
        ...baseField,
        type: 'email',
        ...overrideConfig,
      };
    }
    case 'SIRET': {
      return {
        ...baseField,
        type: 'company',
        displayCompanyInformation: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.[baseField.name] ? { siret: customFields[baseField.name] } : undefined,
        formDataAccessor: `${baseField.name}.siret`,
        ...overrideConfig,
      };
    }
    case 'ARRAY': {
      const columnsConfig: ConfigCustomFieldDto[] = (fieldConfig.columns ?? []).map(column => ({
        key: column.key,
        type: column.type,
        validationRules: column.validationRules,
        requiredFor: column.required ? 'CREATION' : 'NOTHING',
      }));

      return {
        ...baseField,
        type: 'array',
        columns: columnsConfig.flatMap(({ key, type: columnType }) => {
          const column = resolveFieldShortcutFromConfig(
            columnType === 'BOOLEAN' ? ({ name: key, type: 'checkbox' } as Field) : key,
            columnsConfig,
          );
          const label = fieldConfig.display?.columnLabels?.[key];
          return column ? [{ ...column, isCustomField: false, ...(label && { label }) }] : [];
        }),
        maxItems: validationRules?.find(({ option }) => option === 'maxItems')?.value as
          | number
          | undefined,
        ...overrideConfig,
      };
    }
    default: {
      return {
        ...baseField,
        type: 'text',
        ...overrideConfig,
      };
    }
  }
};

type AddressRole = 'address' | 'postalCode' | 'city' | 'country';

// Field types that take part in address autocomplete, and the role each plays.
// The role comes from the field TYPE (declared in config) — no name-sniffing, no flag.
const ADDRESS_ROLE_BY_TYPE: Partial<Record<Field['type'], AddressRole>> = {
  address: 'address',
  postcode: 'postalCode',
  city: 'city',
  country: 'country',
};

type AddressGroupSiblings = {
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
};

const DEFAULT_ADDRESS_GROUP = '__default__';

function warnAmbiguousAddressGroup(groupKey: string, role: AddressRole): void {
  if (!import.meta.env.DEV) {
    return;
  }
  const scope = groupKey === DEFAULT_ADDRESS_GROUP ? 'the default group' : `group "${groupKey}"`;
  // biome-ignore lint/suspicious/noConsole: dev-only misconfiguration signal
  console.warn(
    `[address-autocomplete] multiple "${role}" fields in ${scope}; cross-fill disabled for them. Set a distinct \`addressGroup\` to separate them.`,
  );
}

/**
 * Wires address-autocomplete groups from field TYPES. Every field whose type is
 * address/postcode/city/country is a search anchor; selecting a suggestion cross-fills
 * the group's other fields. Each anchor gets `addressPart` (its role) + `siblingFields`
 * (its group minus itself). A section may hold several independent groups: fields sharing
 * an authored `addressGroup` cluster together, the rest fall into one default group. Names
 * stay unscoped (the leaf re-derives the `assets.{i}.` prefix at runtime).
 *
 * Fail-safe: if a role is claimed by more than one field in the same group (a forgotten
 * `addressGroup`), that role is dropped from every sibling map — the colliding fields
 * self-fill only, so a selection never writes the wrong field silently (dev warns).
 */
export function linkAddressAutocompleteGroup(fields: Field[]): Field[] {
  const members = fields.filter(field => ADDRESS_ROLE_BY_TYPE[field.type] && !field.disabled);
  if (members.length === 0) {
    return fields;
  }

  const groups = new Map<string, AddressGroupSiblings>();
  const collisions = new Map<string, Set<AddressRole>>();
  for (const field of members) {
    const role = ADDRESS_ROLE_BY_TYPE[field.type]!;
    const key = field.addressGroup ?? DEFAULT_ADDRESS_GROUP;
    const group = groups.get(key) ?? {};
    if (group[role] !== undefined) {
      const groupCollisions = collisions.get(key) ?? new Set<AddressRole>();
      groupCollisions.add(role);
      collisions.set(key, groupCollisions);
    } else {
      group[role] = field.name;
    }
    groups.set(key, group);
  }

  for (const [key, roles] of collisions) {
    for (const role of roles) {
      delete groups.get(key)?.[role];
      warnAmbiguousAddressGroup(key, role);
    }
  }

  return fields.map(field => {
    const role = ADDRESS_ROLE_BY_TYPE[field.type];
    if (!role) {
      return field;
    }
    const key = field.addressGroup ?? DEFAULT_ADDRESS_GROUP;
    const collided = collisions.get(key)?.has(role) ?? false;
    const siblingFields = collided ? {} : { ...groups.get(key) };
    delete siblingFields[role];
    return { ...field, addressPart: role, addressGroupKey: key, siblingFields };
  });
}

function expandVehicleInput(
  field: Extract<Field, { type: 'vehicleInput' }>,
  fieldsConfig: ConfigCustomFieldDto[],
): Field[] {
  const identityFields = field.identityFields ?? DEFAULT_VEHICLE_IDENTITY_FIELDS;
  // Only a plate needed to quote is required: later steps can collect it after the brandModel tab
  const registrationNumberRequired =
    fieldsConfig.find(({ key }) => key === 'registrationNumber')?.requiredFor === 'QUOTE';
  const composite: Field = {
    ...field,
    name: field.name ?? 'vehicleInput',
    required: false,
    ignoreInPayload: true,
    registrationNumberRequired,
  };
  const payloadFields: Field[] = identityFields.map(key => {
    const resolved = resolveFieldShortcutFromConfig(key, fieldsConfig);
    const base: Field =
      resolved ??
      ({
        name: key,
        type: 'text',
        isCustomField: true,
        required: key !== 'registrationNumber',
      } as Field);
    // The other identity fields take their required flag from the backend config.
    const autoFillValue = field.identityAutoFillValues?.[key];
    return {
      ...base,
      hidden: true,
      required: key === 'registrationNumber' ? registrationNumberRequired : base.required,
      ...(autoFillValue === undefined ? {} : { autoFillValue }),
    } as Field;
  });
  const manualFlagFields: Field[] = field.manualFlagField
    ? [
        {
          // VehicleInputFormField writes a boolean, not the radio's 'true'/'false' strings
          ...(resolveFieldShortcutFromConfig(
            { name: field.manualFlagField, type: 'checkbox' } as Field,
            fieldsConfig,
          ) ?? ({ name: field.manualFlagField, type: 'checkbox', isCustomField: true } as Field)),
          hidden: true,
          required: false,
          autoFillValue: false,
        } as Field,
      ]
    : [];
  const resolutionField: Field = {
    type: 'checkbox',
    name: VEHICLE_SELECTION_RESOLVED_FIELD,
    required: true,
    ignoreInPayload: true,
    hidden: true,
  } as Field;
  return [composite, ...payloadFields, ...manualFlagFields, resolutionField];
}

export function resolveFieldShortcuts(
  fields: FieldShortcut[],
  fieldsConfig: ConfigCustomFieldDto[],
): Field[] {
  const resolved = fields.flatMap(keyOrField => {
    if (typeof keyOrField !== 'string' && keyOrField.type === 'vehicleInput') {
      return expandVehicleInput(
        keyOrField as Extract<Field, { type: 'vehicleInput' }>,
        fieldsConfig,
      );
    }
    const field = resolveFieldShortcutFromConfig(keyOrField, fieldsConfig);
    return field ? [field] : [];
  });

  return linkAddressAutocompleteGroup(resolved);
}

export function resolveSectionsFieldShortcuts(
  sections: FormSectionsWithShortcuts,
  fieldsConfig: ConfigCustomFieldDto[],
): FormSections {
  return sections.map(section => ({
    ...section,
    fields: resolveFieldShortcuts(section.fields, fieldsConfig),
  }));
}

export function buildAllFieldsFromSections(sections: FormSections) {
  return sections.reduce(
    (acc, section) => {
      acc.push(
        ...section.fields.map(field => {
          // Combine section conditions with field conditions
          const sectionConditions = section.showWhen ?? [];
          const fieldConditions = field.showWhen ?? [];

          // If both section and field have conditions, combine them (ALL must be true)
          const combinedConditions = [...sectionConditions, ...fieldConditions];

          return {
            ...field,
            showWhen: combinedConditions.length > 0 ? combinedConditions : undefined,
          };
        }),
      );
      return acc;
    },
    [] as Array<Field>,
  );
}
